import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet, ArrowRight, Clock, BookOpen, Menu, X } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import { useState } from 'react';

const categoryColors = {
  'Organização': 'bg-blue-100 text-blue-700',
  'Planejamento': 'bg-purple-100 text-purple-700',
  'Poupança': 'bg-emerald-100 text-emerald-700',
  'Segurança Financeira': 'bg-amber-100 text-amber-700',
  'Orçamento': 'bg-rose-100 text-rose-700',
};

function formatDate(iso) {
  const [year, month, day] = iso.split('-');
  const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
  return `${parseInt(day)} de ${months[parseInt(month) - 1]} de ${year}`;
}

export default function BlogPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featured, ...rest] = blogPosts;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b-2 border-slate-200 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1B2A4A]">Bolso</span>
                <span className="text-sm font-medium text-slate-500 ml-1">Sob Controle</span>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-base font-medium text-slate-600 hover:text-[#1B2A4A]">Início</Link>
              <Link to="/blog" className="text-base font-semibold text-[#1B2A4A] border-b-2 border-[#1B2A4A]">Blog</Link>
              <button
                onClick={() => navigate('/auth')}
                className="text-base font-semibold text-[#1B2A4A] hover:underline"
              >
                Entrar
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="h-12 px-6 bg-[#047857] hover:bg-[#059669] text-white rounded-xl text-base font-bold transition-colors"
              >
                Criar Conta Grátis
              </button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-[#1B2A4A]"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200 space-y-3">
              <Link to="/" className="block py-2 text-lg font-medium text-slate-700" onClick={() => setMobileMenuOpen(false)}>Início</Link>
              <Link to="/blog" className="block py-2 text-lg font-semibold text-[#1B2A4A]" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
              <button onClick={() => navigate('/auth')} className="block w-full py-2 text-lg font-medium text-slate-700 text-left">Entrar</button>
              <button
                onClick={() => navigate('/auth')}
                className="block w-full py-3 bg-[#047857] text-white rounded-xl text-lg font-bold text-center"
              >
                Criar Conta Grátis
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={20} className="text-[#047857]" />
            <span className="text-base font-semibold text-[#047857]">Blog</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1B2A4A] mb-4">
            Finanças que fazem sentido
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl">
            Artigos práticos sobre organização financeira, poupança e planejamento — escritos para quem quer controle real do próprio dinheiro.
          </p>
        </div>
      </section>

      {/* Featured article */}
      <section className="px-4 md:px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          <Link
            to={`/blog/${featured.slug}`}
            className="group block bg-white border-2 border-slate-200 rounded-2xl overflow-hidden hover:border-[#1B2A4A] hover:shadow-lg transition-all"
          >
            <div className="p-8 md:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${categoryColors[featured.categoria] || 'bg-slate-100 text-slate-700'}`}>
                  {featured.categoria}
                </span>
                <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                  <Clock size={14} />
                  {featured.tempoLeitura}
                </span>
                <span className="text-sm text-slate-500">{formatDate(featured.dataPublicacao)}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1B2A4A] mb-3 group-hover:underline">
                {featured.titulo}
              </h2>
              <p className="text-lg text-slate-600 mb-6 max-w-3xl">{featured.resumo}</p>
              <span className="inline-flex items-center gap-2 text-base font-semibold text-[#047857]">
                Ler artigo <ArrowRight size={18} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Article grid */}
      <section className="px-4 md:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group block bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-[#1B2A4A] hover:shadow-md transition-all"
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${categoryColors[post.categoria] || 'bg-slate-100 text-slate-700'}`}>
                    {post.categoria}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                    <Clock size={14} />
                    {post.tempoLeitura}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2 group-hover:underline">
                  {post.titulo}
                </h3>
                <p className="text-base text-slate-600 mb-4">{post.resumo}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">{formatDate(post.dataPublicacao)}</span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#047857]">
                    Ler <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B2A4A] py-16 px-4 md:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Pronto para colocar em prática?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Crie sua conta gratuita e comece a organizar suas finanças hoje mesmo.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="h-14 px-8 bg-[#047857] hover:bg-[#059669] text-white rounded-xl text-lg font-bold transition-colors"
          >
            Criar Conta Grátis
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-slate-200 py-8 px-4 md:px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1B2A4A] rounded-lg flex items-center justify-center">
              <Wallet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-[#1B2A4A]">Bolso Sob Controle</span>
          </Link>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Bolso Sob Controle. Todos os direitos reservados.
          </p>
          <div className="flex gap-4">
            <Link to="/" className="text-sm text-slate-500 hover:text-[#1B2A4A]">Início</Link>
            <Link to="/blog" className="text-sm text-slate-500 hover:text-[#1B2A4A]">Blog</Link>
            <Link to="/auth" className="text-sm text-slate-500 hover:text-[#1B2A4A]">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
